import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../Common/Button';
import { Badge } from '../Common/Badge';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { ScheduleForm } from './ScheduleForm';
import { showToast } from '../../hooks/useToast';
import { useAuthState } from '../Layout/AuthGate';

export function ScheduleList() {
  const auth = useAuthState();
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    api.listSchedules()
      .then(r => setSchedules(r.schedules))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id: string) => {
    if (!window.confirm('Deactivate this schedule? Existing deliveries will stop.')) return;
    try {
      await api.deleteSchedule(id);
      showToast('success', 'Schedule deactivated');
      load();
    } catch (err: any) {
      showToast('error', err.message ?? 'Delete failed');
    }
  };

  const runNow = async (id: string) => {
    try {
      await api.runScheduleNow(id);
      showToast('success', 'Schedule queued — check inbox in a moment');
    } catch (err: any) {
      showToast('error', err.message ?? 'Run-now failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-text-muted">{schedules.length} schedule{schedules.length === 1 ? '' : 's'}</div>
        <Button variant="primary" size="sm" onClick={() => setCreating(true)}>New schedule</Button>
      </div>

      {loading && <LoadingSpinner label="Loading schedules…" />}

      {!loading && schedules.length === 0 && (
        <div className="text-sm text-text-muted">No schedules yet.</div>
      )}

      {!loading && schedules.length > 0 && (
        <div className="space-y-2">
          {schedules.map(s => {
            const recipients = (() => {
              try { return JSON.parse(s.recipients); } catch { return []; }
            })();
            return (
              <div key={s.id} className="bg-surface border border-border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-sm font-medium text-text-primary truncate">{s.name}</div>
                      {s.isActive ? <Badge variant="success">active</Badge> : <Badge variant="neutral">inactive</Badge>}
                      <Badge variant="neutral">{s.exportFormat}</Badge>
                    </div>
                    <div className="text-xs text-text-muted">
                      {s.frequency} · {s.timeUtc} UTC · {s.reportName}
                    </div>
                    <div className="text-[10px] text-text-hint mt-1">
                      {recipients.length} recipient{recipients.length === 1 ? '' : 's'} · next: {s.nextRunAt ? new Date(s.nextRunAt).toLocaleString() : '—'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {auth.role === 'admin' && <Button size="sm" onClick={() => runNow(s.id)}>Run now</Button>}
                    <Button size="sm" onClick={() => setEditing(s)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => remove(s.id)}>Deactivate</Button>
                  </div>
                </div>
                {s.lastRunAt && (
                  <div className="text-[10px] text-text-hint mt-2">
                    Last run: {new Date(s.lastRunAt).toLocaleString()} — {s.lastRunStatus}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ScheduleForm open={creating} onClose={() => setCreating(false)} onSaved={load} />
      <ScheduleForm open={!!editing} schedule={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
